import { FileBarChart2, FileText, KeyRound } from "lucide-react";

/**
 * The three tiles at the top of the CMA page, and how each one runs, copied
 * from Property Data (Christian, Monday 13066303151):
 *
 *   Sales CMA Report / Rental CMA Report
 *     Search Options > Results Summary > Select Records > Report Setup
 *   Property Price Statement (the statutory s47AF form)
 *     Search Options > Results Summary > Select Comparables (max 3) > Statement
 *
 * Keys match CmaReport.TYPE_CHOICES on the backend.
 */
export const CMA_REPORT_TYPES = [
  {
    key: "sales",
    label: "Sales CMA Report",
    blurb: "Compare recent sales to price a property for a vendor.",
    icon: FileBarChart2,
    searchHeading: "Enter a CMA Report target address...",
    startLabel: "Start CMA Report",
    dateLabel: "Sale Date",
    defaultMonths: 12,
    rental: false,
    statement: false,
  },
  {
    key: "price_statement",
    label: "Property Price Statement",
    blurb: "The Section 47AF statement, published as a public link.",
    icon: FileText,
    searchHeading: "Enter Property Price Statement target address...",
    startLabel: "Start Report",
    dateLabel: "Sale Date",
    defaultMonths: 6,
    defaultRadiusKm: 2,
    rental: false,
    statement: true,
  },
  {
    key: "rental",
    label: "Rental CMA Report",
    blurb: "Compare leased properties to price a rental for a landlord.",
    icon: KeyRound,
    searchHeading: "Enter a Rental CMA Report target address...",
    startLabel: "Start CMA Report",
    dateLabel: "Rental Date",
    defaultMonths: 12,
    rental: true,
    statement: false,
  },
];

export const DEFAULT_CMA_REPORT_TYPE = "sales";

export const getCmaReportType = (key) =>
  CMA_REPORT_TYPES.find((t) => t.key === key) || CMA_REPORT_TYPES[0];

// The tabs on Select Records, in Property Data's order.
export const RECORD_GROUPS = [
  { key: "history", label: "Property History" },
  { key: "street", label: "Comparable Sales (Street)", rentalLabel: "Comparable Rentals (Street)" },
  { key: "suburb", label: "Comparable Sales (Suburb)", rentalLabel: "Comparable Rentals (Suburb)" },
  { key: "listing", label: "Current Listings" },
];

export const groupLabel = (group, rental) =>
  (rental && group.rentalLabel) || group.label;

// Local calendar date, not UTC: toISOString() would roll a Melbourne evening
// over to tomorrow's date.
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** The Search Options a fresh report starts with, same defaults as Property Data. */
export const defaultCriteria = (typeKey) => {
  const type = getCmaReportType(typeKey);
  const to = new Date();
  const from = new Date(to);
  from.setMonth(from.getMonth() - type.defaultMonths);
  return {
    date_from: iso(from),
    date_to: iso(to),
    price_min: "",
    price_max: "",
    include_surrounding: Boolean(type.defaultRadiusKm),
    radius_km: type.defaultRadiusKm || "",
    property_type: "",
    beds_min: "",
    beds_max: "",
    baths_min: "",
    baths_max: "",
    cars_min: "",
    cars_max: "",
    land_min: "",
    land_max: "",
    ...(type.statement ? { location: "metro" } : {}),
  };
};

// Property Data's "Quick set date range" choices.
export const QUICK_DATE_RANGES = [
  { label: "Last week", days: 7 },
  { label: "Last month", months: 1 },
  { label: "Last 3 months", months: 3 },
  { label: "Last 6 months", months: 6 },
  { label: "Last 9 months", months: 9 },
  { label: "Last 12 months", months: 12 },
  { label: "Last 18 months", months: 18 },
  { label: "Last 2 years", months: 24 },
  { label: "Last 3 years", months: 36 },
  { label: "Last 5 years", months: 60 },
];

export const quickRange = ({ days, months }) => {
  const to = new Date();
  const from = new Date(to);
  if (days) from.setDate(from.getDate() - days);
  if (months) from.setMonth(from.getMonth() - months);
  return { date_from: iso(from), date_to: iso(to) };
};

/** Drop blank values so the saved criteria only hold what the agent set. */
export const cleanCriteria = (criteria) =>
  Object.fromEntries(
    Object.entries(criteria || {}).filter(([, v]) => v !== "" && v !== null && v !== undefined),
  );

export const PROPERTY_TYPES = [
  "house", "apartment", "unit", "townhouse", "villa", "land", "blocks_of_units",
];

// Report Setup > Report Sections, as on Property Data. All on by default.
export const REPORT_SECTIONS = [
  { key: "agent_profile", label: "Agent Profile" },
  { key: "property_details", label: "Property Details" },
  { key: "market", label: "Demographics & Median Prices" },
  { key: "search_criteria", label: "Search Criteria" },
  { key: "history", label: "Property History", group: "history" },
  { key: "suburb", label: "Comparable Sales (Suburb)", group: "suburb" },
  { key: "street", label: "Comparable Sales (Street)", group: "street" },
  { key: "listing", label: "Current Listings", group: "listing" },
  { key: "estimate", label: "Price Estimate" },
];

export const REPORT_TEMPLATES = [
  { key: "modern", label: "Modern", hint: "Colour band cover" },
  { key: "classic", label: "Classic", hint: "Centred serif headings" },
  { key: "minimal", label: "Minimal", hint: "Plain and light" },
];

export const SALE_METHODS = [
  { key: "private_sale", label: "Private sale" },
  { key: "auction", label: "Auction" },
  { key: "fixed_date", label: "Fixed-date sale" },
];
